import { App, Button, Form } from "antd";
import { ArrowLeft } from "iconsax-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomModal,
  CustomSelect,
  CustomTypography,
} from "sipli-fleet-core/components";
import { cn } from "sipli-fleet-core/utils/cn";
import {
  useMutationAddDeviceMapping,
  useMutationDeleteDeviceMapping,
  useMutationUpdateDeviceMapping,
  useQueryGetRelayStatusLookups,
  useQueryGetSwitchOnOffStateTypeLookups,
  useQueryGetTemperatureTypeLookups,
} from "src/queries/mapping.queries";
import {
  useMutationCreateMappingPresetMapping,
  useMutationDeleteMappingPresetMapping,
  useMutationUpdateMappingPresetMapping,
} from "src/queries/mappingPreset.queries";
import {
  addMappingOptions,
  applyFilterPreview,
  buildMappingSubmitPayload,
  deleteMappingWithFeedback,
  getIncomingOptions,
  getInitialMappingValuesFromCardContext,
  getDefaultMappingFieldValues,
  getMappingName,
  getSwitchStateStaticOptions,
  makeFilter,
  normalizeFormPrimitive,
  toLookupOptions,
} from "./mapping.helpers";
import type { IncomingValuesResponse } from "src/services/mappingService";
import type {
  MappingAddEditValues,
  MappingCardContext,
  MappingFilterRecord,
  MappingRecord,
} from "./mapping.types";
import MappingDelete from "./MappingDelete";
import { MappingEditorFields } from "./MappingEditorFields";

interface MappingAddEditProps {
  open: boolean;
  unitId: string | number;
  incomingValues: IncomingValuesResponse;
  mappings: MappingRecord[];
  context?: MappingCardContext | null;
  mode?: "device" | "preset";
  presetId?: string | number;
  deviceModelId?: string | number;
  isBasePreset?: boolean;
  onClose: () => void;
}

const ADD_MAPPING_STEPS = [
  { id: 1, labelKey: "setting_mapping" },
  { id: 2, labelKey: "filter" },
];

const MappingAddEdit = ({
  open,
  unitId,
  incomingValues,
  mappings,
  context,
  mode = "device",
  presetId,
  deviceModelId,
  isBasePreset,
  onClose,
}: MappingAddEditProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<MappingAddEditValues>();
  const [isSecondPage, setIsSecondPage] = useState(false);
  const [editingMapping, setEditingMapping] = useState<MappingRecord | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<MappingRecord | null>(null);
  const [filterDeleteUniqueId, setFilterDeleteUniqueId] = useState<string>();
  const [filters, setFilters] = useState<MappingFilterRecord[]>([]);
  const [formValues, setFormValues] = useState<MappingAddEditValues>({});
  const [selectedMappingType, setSelectedMappingType] = useState<string>();
  const lastIgnitionWarningKeyRef = useRef("");
  const initializedOpenKeyRef = useRef("");

  const addMutation = useMutationAddDeviceMapping();
  const updateMutation = useMutationUpdateDeviceMapping();
  const deleteMutation = useMutationDeleteDeviceMapping();
  const createPresetMappingMutation = useMutationCreateMappingPresetMapping();
  const updatePresetMappingMutation = useMutationUpdateMappingPresetMapping();
  const deletePresetMappingMutation = useMutationDeleteMappingPresetMapping();
  const relayLookupQuery = useQueryGetRelayStatusLookups(open);
  const switchLookupQuery = useQueryGetSwitchOnOffStateTypeLookups(open);

  const incomingOptions = useMemo(() => getIncomingOptions(incomingValues), [incomingValues]);
  const watchedMappingType = Form.useWatch("selectMapping", form);
  const watchedValueId = Form.useWatch("value", form);
  const watchedLinkedToFuel = Form.useWatch("linkedToFuel", form);
  const watchedTargetCheckbox = Form.useWatch("targetCheckbox", form);
  const watchedValues = Form.useWatch([], form) as MappingAddEditValues | undefined;
  const selectedIncomingOption = incomingOptions.find((item) => item.id === watchedValueId);
  const effectiveMappingType = watchedMappingType ?? selectedMappingType;
  const temperatureTypeLookupQuery = useQueryGetTemperatureTypeLookups(
    open && effectiveMappingType === "temperature"
  );
  const selectedFieldPacketCode = selectedIncomingOption?.packetCode ?? context?.packetCode ?? "";
  const isPresetMode = mode === "preset";
  const isEditing = Boolean(editingMapping);
  const isLoading =
    addMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    createPresetMappingMutation.isPending ||
    updatePresetMappingMutation.isPending ||
    deletePresetMappingMutation.isPending;

  useEffect(() => {
    if (!open) return;
    const openKey = context
      ? `${context.parentKey}:${context.childKey}:${context.packetCode}`
      : "main-add";
    if (initializedOpenKeyRef.current === openKey) return;

    const initialValues = getInitialMappingValuesFromCardContext(context ?? null, incomingOptions);
    form.setFieldsValue(initialValues);
    setFilters([]);
    setFormValues(initialValues);
    setIsSecondPage(false);
    setEditingMapping(null);
    setSelectedMappingType(undefined);
    initializedOpenKeyRef.current = openKey;
  }, [context, form, incomingOptions, open]);

  useEffect(() => {
    if (!selectedIncomingOption || editingMapping) return;
    form.setFieldValue("lastValue", selectedIncomingOption.lastValue);
    setFormValues((prev) => ({
      ...prev,
      lastValue: selectedIncomingOption.lastValue,
    }));
  }, [editingMapping, form, selectedIncomingOption]);

  const closeModal = () => {
    form.resetFields();
    setFilters([]);
    setFormValues({});
    setIsSecondPage(false);
    setEditingMapping(null);
    setSelectedMappingType(undefined);
    setDeleteRecord(null);
    setFilterDeleteUniqueId(undefined);
    initializedOpenKeyRef.current = "";
    onClose();
  };

  const showIgnitionAlreadyAppliedWarning = () => {
    message.warning(
      t("ignition_start_stop_events_has_already_applied_this_mapping", {
        defaultValue: "Ignition start/stop events has already applied this mapping",
      })
    );
  };

  const isIgnitionAlreadyAppliedOnSelectedField = (packetCode = selectedFieldPacketCode) => {
    if (!packetCode) return false;

    return mappings.some(
      (item) =>
        item.mappingTypeId === "ignition_start_stop_events" &&
        String(item.packetCode ?? "") === String(packetCode) &&
        (!editingMapping || String(item.id) !== String(editingMapping.id))
    );
  };

  const isIgnitionSelectionBlocked =
    !isEditing &&
    effectiveMappingType === "ignition_start_stop_events" &&
    Boolean(selectedFieldPacketCode) &&
    isIgnitionAlreadyAppliedOnSelectedField(selectedFieldPacketCode);

  const validateIgnitionMappingForSelectedField = (
    mappingTypeId = form.getFieldValue("selectMapping") ?? selectedMappingType,
    packetCode = selectedFieldPacketCode
  ) => {
    if (mappingTypeId !== "ignition_start_stop_events") return true;
    if (!packetCode) return true;
    if (!isIgnitionAlreadyAppliedOnSelectedField(packetCode)) return true;

    showIgnitionAlreadyAppliedWarning();
    return false;
  };

  useEffect(() => {
    if (!open || isEditing) return;

    const warningKey = `${effectiveMappingType}:${selectedFieldPacketCode}`;

    if (!isIgnitionSelectionBlocked) {
      lastIgnitionWarningKeyRef.current = "";
      return;
    }

    if (lastIgnitionWarningKeyRef.current === warningKey) return;

    showIgnitionAlreadyAppliedWarning();
    lastIgnitionWarningKeyRef.current = warningKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMappingType, isEditing, isIgnitionSelectionBlocked, open, selectedFieldPacketCode]);

  const handleSelectMapping = (mappingTypeId: string) => {
    const resetValues = getDefaultMappingFieldValues(mappingTypeId);

    form.setFieldsValue({
      ...resetValues,
      selectMapping: mappingTypeId,
    });
    setSelectedMappingType(mappingTypeId);
    setFormValues((prev) => ({
      ...prev,
      ...resetValues,
      selectMapping: mappingTypeId,
    }));
    setEditingMapping(null);
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;

    try {
      if (isPresetMode && presetId) {
        await deletePresetMappingMutation.mutateAsync({
          mappingId: deleteRecord.id,
          presetId,
        });
        message.success(
          t("preset_mapping_deleted_successfully", {
            defaultValue: "Preset Mapping Deleted Successfully!",
          })
        );
        setDeleteRecord(null);
        closeModal();
        return;
      }

      await deleteMappingWithFeedback({
        mapping: deleteRecord,
        unitId,
        deleteMapping: deleteMutation.mutateAsync,
        message,
        t,
        onSuccess: () => {
          setDeleteRecord(null);
          closeModal();
        },
      });
    } catch {
      message.error(t("error"));
    }
  };

  const syncFilters = (nextFilters: MappingFilterRecord[]) => {
    setFilters(nextFilters);
    form.setFieldValue("filterType", nextFilters);
    setFormValues((prev) => ({
      ...prev,
      filterType: nextFilters,
    }));
  };

  const handleAddFilter = (filterId: string) => {
    syncFilters([...filters, makeFilter(filterId)]);
  };

  const handleRemoveFilter = (uniqueId?: string) => {
    syncFilters(filters.filter((filter) => (filter.uniqueId ?? filter.id) !== uniqueId));
  };

  const handleConfirmRemoveFilter = () => {
    handleRemoveFilter(filterDeleteUniqueId);
    setFilterDeleteUniqueId(undefined);
  };

  const handleReorderFilters = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex < 0 || destinationIndex < 0 || sourceIndex === destinationIndex) return;

    const nextFilters = [...filters];
    const [movedFilter] = nextFilters.splice(sourceIndex, 1);
    if (!movedFilter) return;

    nextFilters.splice(destinationIndex, 0, movedFilter);
    syncFilters(nextFilters);
  };

  const handleSubmit = async () => {
    await form.validateFields();

    const allValues = form.getFieldsValue(true) as MappingAddEditValues;
    const values: MappingAddEditValues = {
      ...allValues,
      selectMapping: allValues.selectMapping ?? selectedMappingType,
      value: allValues.value ?? form.getFieldValue("value"),
      filterType: filters,
    };

    const incomingOption = incomingOptions.find((item) => item.id === values.value);
    if (
      !validateIgnitionMappingForSelectedField(values.selectMapping, incomingOption?.packetCode)
    ) {
      return;
    }

    const { payload, updatePayload } = buildMappingSubmitPayload(
      values,
      incomingOptions,
      unitId,
      editingMapping
    );

    try {
      if (isPresetMode && presetId) {
        const presetFields = {
          presetId,
          ...(deviceModelId ? { deviceType: deviceModelId } : {}),
          ...(isBasePreset ? { base: isBasePreset } : {}),
        };

        if (editingMapping) {
          await updatePresetMappingMutation.mutateAsync({
            id: editingMapping.id,
            presetId,
            payload: {
              ...updatePayload,
              ...(deviceModelId !== undefined && deviceModelId !== null
                ? { deviceType: deviceModelId }
                : {}),
              isPreset: true,
            },
          });
          message.success(
            t("preset_mapping_updated_successfully", {
              defaultValue: "Preset Mapping Updated Successfully!",
            })
          );
        } else {
          await createPresetMappingMutation.mutateAsync({
            ...updatePayload,
            ...presetFields,
          });
          message.success(
            t("preset_mapping_added_successfully", {
              defaultValue: "Preset Mapping Added Successfully!",
            })
          );
        }
        closeModal();
      } else if (editingMapping) {
        await updateMutation.mutateAsync({
          id: editingMapping.id,
          unitId,
          payload: updatePayload,
        });
        message.success(t("mapping_updated_successfully"));
      } else {
        await addMutation.mutateAsync(payload);
        message.success(t("mapping_added_successfully"));
      }
      closeModal();
    } catch {
      message.error(t("error"));
    }
  };

  const previewValue = useMemo(() => {
    const values = {
      ...formValues,
      ...form.getFieldsValue(),
      ...(watchedValues ?? {}),
    };
    let result = values.lastValue;

    filters.forEach((filter) => {
      result = normalizeFormPrimitive(applyFilterPreview(filter, result, values));
    });

    return result;
  }, [filters, form, formValues, watchedValues]);

  const handleNextStep = async () => {
    await form.validateFields();

    if (!validateIgnitionMappingForSelectedField(undefined, selectedFieldPacketCode)) {
      return;
    }

    setIsSecondPage(true);
  };

  const handleModalOk = async () => {
    if (isEditing || isSecondPage) {
      await handleSubmit();
      return;
    }

    await handleNextStep();
  };

  const handleStepClick = (stepId: number) => {
    if (stepId === 1) {
      setIsSecondPage(false);
      return;
    }

    if (!isSecondPage) {
      void handleNextStep().catch(() => undefined);
    }
  };

  const renderAddMappingSteps = () => (
    <div className="mb-8 flex items-center gap-3">
      {ADD_MAPPING_STEPS.map((step, index) => {
        const isActive = isSecondPage ? step.id === 2 : step.id === 1;

        return (
          <div key={step.id} className="flex flex-1 items-center">
            <button
              type="button"
              onClick={() => handleStepClick(step.id)}
              className="flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left transition-opacity hover:opacity-80"
            >
              <div
                className={cn(
                  "flex h-[36px] w-[36px] items-center justify-center rounded-full text-sm font-semibold transition-all",
                  {
                    "bg-primary text-white": isActive,
                    "border-2 border-gray3 text-gray9": !isActive,
                  }
                )}
              >
                {step.id}
              </div>

              <CustomTypography.CustomText
                className={cn({
                  "!text-[15.75px] !font-semibold !text-black": isActive,
                  "!text-[14px] !text-gray9 ": !isActive,
                  "cursor-pointer": true,
                })}
              >
                {t(step.labelKey)}
              </CustomTypography.CustomText>
            </button>

            {index !== ADD_MAPPING_STEPS.length - 1 && (
              <div className="mx-5 h-[1px] flex-1 bg-black" />
            )}
          </div>
        );
      })}
    </div>
  );

  const mappingEditorSharedProps = {
    incomingOptions,
    watchedFilters: filters,
    watchedMappingType: effectiveMappingType,
    watchedLinkedToFuel: Boolean(watchedLinkedToFuel),
    watchedTargetCheckbox: Boolean(watchedTargetCheckbox),
    previewValue,
    relayOptions: toLookupOptions(relayLookupQuery.data),
    switchOptions: toLookupOptions(switchLookupQuery.data).length
      ? toLookupOptions(switchLookupQuery.data)
      : getSwitchStateStaticOptions(t),
    temperatureTypeOptions: toLookupOptions(temperatureTypeLookupQuery.data),
    mappings,
    editingMapping,
    onAddFilter: handleAddFilter,
    onRemoveFilter: setFilterDeleteUniqueId,
    onReorderFilters: handleReorderFilters,
  };

  const handleFooterCancel = () => {
    if (!isEditing && isSecondPage) {
      setIsSecondPage(false);
      return;
    }

    closeModal();
  };

  const footerCancelText = !isEditing && isSecondPage ? t("back") : t("cancel");
  const footerOkText = isEditing ? t("update") : isSecondPage ? t("save") : t("next");
  const isPrimaryActionDisabled = !isEditing && isIgnitionSelectionBlocked;

  return (
    <>
      <CustomModal
        open={open}
        title={isEditing ? t("mappingDetails") : undefined}
        width={734}
        className="add-mapping-modal"
        onCancel={closeModal}
        footer={null}
        destroyOnHidden
        centered
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="pt-2 add-mapping-form"
          onValuesChange={(_, allValues) => setFormValues(allValues as MappingAddEditValues)}
        >
          {!isEditing ? (
            <>
              {renderAddMappingSteps()}

              <CustomTypography.CustomText className="!text-[23px] !font-bold !leading-[28px] mb-4">
                {t("add_mapping_title")}
              </CustomTypography.CustomText>

              {!isSecondPage ? (
                <div className="mt-8">
                  <Form.Item
                    name="selectMapping"
                    label={t("mapping", { defaultValue: "Mapping" })}
                    rules={[
                      {
                        required: true,
                        message: t("mapping_is_required", {
                          defaultValue: "Mapping is required",
                        }),
                      },
                    ]}
                  >
                    <CustomSelect
                      options={addMappingOptions.map((option) => ({
                        value: option.id,
                        label: getMappingName(option.id, t),
                      }))}
                      onChange={handleSelectMapping}
                      showSearch
                      optionFilterProp="label"
                      placeholder={t("select_option")}
                      className="common-select"
                    />
                  </Form.Item>

                  <MappingEditorFields {...mappingEditorSharedProps} variant="add-step-one" />
                </div>
              ) : (
                <MappingEditorFields {...mappingEditorSharedProps} variant="add-step-two" />
              )}
            </>
          ) : (
            <div>
              <Button
                type="link"
                className="!px-0"
                icon={<ArrowLeft size={16} />}
                onClick={() => {
                  setIsSecondPage(false);
                  setEditingMapping(null);
                  setSelectedMappingType(undefined);
                  form.setFieldValue("selectMapping", undefined);
                  form.setFieldValue("filterType", []);
                  setFilters([]);
                  setFormValues((prev) => ({
                    ...prev,
                    selectMapping: undefined,
                    filterType: [],
                  }));
                }}
              >
                {t("back_to_options")}
              </Button>
              <CustomTypography.CustomTitle level={4} className="!mt-2">
                {getMappingName(effectiveMappingType, t)}
              </CustomTypography.CustomTitle>

              <MappingEditorFields {...mappingEditorSharedProps} />
            </div>
          )}
        </Form>

        <CustomActionButtons
          onCancel={handleFooterCancel}
          loading={isLoading}
          disabled={isPrimaryActionDisabled}
          cancelBtnLabel={footerCancelText}
          okBtnLabel={footerOkText}
          onOk={handleModalOk}
          className="mt-8"
        />
      </CustomModal>
      <MappingDelete
        open={Boolean(deleteRecord)}
        isLoading={deleteMutation.isPending}
        zIndex={2200}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDelete}
      />
      <MappingDelete
        open={Boolean(filterDeleteUniqueId)}
        isLoading={false}
        zIndex={2200}
        title={t("delete_filter", { defaultValue: "Delete Filter" })}
        onCancel={() => setFilterDeleteUniqueId(undefined)}
        onConfirm={handleConfirmRemoveFilter}
      />
    </>
  );
};

export default MappingAddEdit;
